import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const testimonials = [
  {
    quote: 'Thoughtic helped us scale our dev team with highly skilled engineers. Their responsiveness and quality of talent were exceptional.',
    name: 'Sarah Johnson',
    role: 'VP of Operations',
    company: 'TechVision Inc.',
    initials: 'TV',
    logoColor: 'bg-navy-900',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20female%20executive%20business%20woman%20wearing%20navy%20blazer%20confident%20smile%20corporate%20headshot%20portrait%20clean%20white%20background%20studio%20lighting%20high%20quality%20professional%20photography&width=80&height=80&seq=testimonial-avatar-1&orientation=squarish',
  },
  {
    quote: 'A reliable staffing partner that understands both business needs and technical requirements. Consistently delivers top-tier talent.',
    name: 'Michael Chen',
    role: 'CTO',
    company: 'SecureData Solutions',
    initials: 'SD',
    logoColor: 'bg-teal-600',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20male%20asian%20technology%20executive%20wearing%20black%20shirt%20confident%20expression%20corporate%20headshot%20portrait%20clean%20white%20background%20studio%20lighting%20high%20quality%20professional%20photography&width=80&height=80&seq=testimonial-avatar-2&orientation=squarish',
  },
  {
    quote: 'Working with Thoughtic transformed how we approach hiring. Pre-vetted candidates and fast onboarding saved us weeks.',
    name: 'Emily Rodriguez',
    role: 'Product Manager',
    company: 'InnovateLabs',
    initials: 'IN',
    logoColor: 'bg-emerald-600',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20female%20hispanic%20product%20manager%20wearing%20white%20blouse%20warm%20smile%20corporate%20headshot%20portrait%20clean%20white%20background%20studio%20lighting%20high%20quality%20professional%20photography&width=80&height=80&seq=testimonial-avatar-3&orientation=squarish',
  },
  {
    quote: 'The dedicated team model allowed us to scale product development without any overhead. Truly a strategic partnership.',
    name: 'David Thompson',
    role: 'CFO',
    company: 'FinanceFirst Group',
    initials: 'FF',
    logoColor: 'bg-amber-500',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20male%20finance%20executive%20wearing%20gray%20suit%20confident%20expression%20corporate%20headshot%20portrait%20clean%20white%20background%20studio%20lighting%20high%20quality%20professional%20photography&width=80&height=80&seq=testimonial-avatar-4&orientation=squarish',
  },
  {
    quote: 'We needed specialized Healthcare IT expertise urgently. Thoughtic delivered qualified professionals within days. Outstanding service.',
    name: 'Lisa Anderson',
    role: 'Director of IT',
    company: 'GlobalTech Enterprises',
    initials: 'GT',
    logoColor: 'bg-sky-600',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20female%20it%20director%20wearing%20lime%20green%20blazer%20confident%20smile%20corporate%20headshot%20portrait%20clean%20white%20background%20studio%20lighting%20high%20quality%20professional%20photography&width=80&height=80&seq=testimonial-avatar-5-v2&orientation=squarish',
  },
  {
    quote: 'Thoughtic is our go-to partner for offshore development. Their talent pool is deep and integration with our teams is seamless.',
    name: 'James Mitchell',
    role: 'CEO',
    company: 'GrowthTech Partners',
    initials: 'GP',
    logoColor: 'bg-violet-600',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20male%20ceo%20executive%20leader%20wearing%20charcoal%20suit%20confident%20expression%20corporate%20headshot%20portrait%20clean%20white%20background%20studio%20lighting%20high%20quality%20professional%20photography&width=80&height=80&seq=testimonial-avatar-6&orientation=squarish',
  },
];

function StarRating() {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>
      ))}
      <span className="text-[10px] font-semibold text-gray-400 ml-1">5.0</span>
    </div>
  );
}

export default function Testimonials() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  return (
    <section id="testimonials" ref={ref} className="py-12 md:py-16 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.6 }}
          className="text-center mb-8 md:mb-10"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-full mb-3"
          >
            <i className="ri-chat-quote-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
            <span className="text-xs font-medium text-gray-700">What Our Clients Say</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6 mb-3"
          >
            Trusted by Teams
            <span className="text-primary-600"> Worldwide</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="text-sm md:text-base text-gray-500 max-w-xl mx-auto"
          >
            Join businesses that have scaled their engineering teams with Thoughtic Solutions.
          </motion.p>
        </motion.div>

        {/* Compact Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {testimonials.map((t, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ ...springTransition, duration: 0.5, delay: index * 0.06 }}
              className="group relative bg-gray-50/80 rounded-xl border border-gray-100 p-5 md:p-6 hover:bg-white hover:border-primary-200 hover:shadow-lg hover:shadow-primary-500/5 transition-all duration-400"
            >
              {/* Stars */}
              <div className="mb-3">
                <StarRating />
              </div>

              {/* Quote */}
              <p className="text-sm text-gray-700 leading-relaxed mb-4 line-clamp-3">
                &ldquo;{t.quote}&rdquo;
              </p>

              {/* Author row */}
              <div className="flex items-center gap-3 pt-3 border-t border-gray-200/60">
                <div className="relative">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-9 h-9 rounded-lg object-cover border border-gray-100"
                    loading="lazy"
                    draggable={false}
                  />
                  <div className={`absolute -bottom-1.5 -right-1.5 w-4 h-4 flex items-center justify-center rounded-full ${t.logoColor} text-white text-[7px] font-bold border-2 border-white shadow-sm`}>
                    {t.initials[0]}
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-navy-900 truncate">{t.name}</p>
                  <p className="text-xs text-gray-500 truncate">{t.role} · {t.company}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}